import React, {
  Component,
  ChangeEventHandler,
  FormEvent,
  Fragment,
  createRef,
} from 'react';
import classNames from 'classnames';
import { RoleId } from '@wix/members-domain-ts';
import { BadgeList } from '@wix/members-badge-lib';

import {
  BaseComponentProps,
  AdditionalActionId,
  DataHook,
  ElementId,
  Position,
  ProfileLayout,
} from '../../../../../types';
import { titleMaxLength } from '../../../../../constants/limits';
import {
  getCoverUrl,
  getCoverImageStyle,
} from '../../../../../services/cover-utils';
import {
  absorbArguments,
  forwardTargetValue,
  forwardResizedImage,
  forwardCoverRepositionAttributes,
} from '../../../../../services/event-handler';
import { getElementId } from '../../../../../services/html-attributes';
import { maybeOpenContactsWindow } from '../../../../../services/navigation';
import LoaderOverlay from '../../../../../common/components/LoaderOverlay';
import Button from '../../../../../common/components/Button';
import { RepositionButton } from '../../../../../common/components/RepositionButton';
import Avatar from '../../../../../common/components/Avatar';
import MemberName from '../../../../../common/components/MemberName';
import LimitCounter from '../../../../../common/components/LimitCounter';
import RoleIcons from '../../../../../common/components/RoleIcons';
import Blocked from '../../../../../common/components/Blocked';
import FollowButton from '../../../../../common/components/FollowButton';
import MemberMoreButton from '../../../../../common/components/MemberMoreButton';
import styles from './DesktopResponsiveWidget.scss';

type SubmitButtonClickHandler = (event: FormEvent<HTMLElement>) => void;

const WIDE_WIDGET_BREAKPOINT = 1001;

interface RenderButtonProps {
  dataHook?: DataHook;
  className?: string;
  title: string;
  isSecondary?: boolean;
  isDisabled?: boolean;
  onClick: SubmitButtonClickHandler;
}

const defaultRenderButtonProps: Partial<RenderButtonProps> = {
  className: '',
  isSecondary: false,
  isDisabled: false,
};

class DesktopResponsiveWidget extends Component<BaseComponentProps> {
  private coverRef = createRef<HTMLDivElement>();
  private memberNameInputRef = createRef<HTMLInputElement>();

  state = { isMovingCover: false };

  componentDidUpdate({ profilePage }: BaseComponentProps) {
    const { isEditing } = this.props.profilePage;
    const memberNameInput = this.memberNameInputRef.current;

    if (profilePage.isEditing !== isEditing && memberNameInput) {
      memberNameInput.focus();
    }
  }

  render = () => {
    const { site, profilePage, globalSettings, computed, host } = this.props;
    const { badgesSettings, inCommunity, showAsBlocked, showTitle } = computed;
    const { isEditing, isSaving, isCoverRepositionMode } = profilePage;
    const { showFollowers } = globalSettings;
    const { isSocial } = site;

    const containerClasses = {
      [styles.profile]: true,
      [styles.editMode]: isEditing,
      [styles.nonSocial]: !isSocial,
      [this.getWidgetSizeClass(host?.dimensions)]: true,
    };

    return (
      <div data-hook={DataHook.HorizontalWidget} className={styles.box}>
        <div className={styles.border} />
        <div className={classNames(containerClasses)}>
          {isSaving && this.renderLoader()}
          {this.renderCover()}
          {!isCoverRepositionMode && (
            <div className={styles.layout}>
              {this.renderPhoto()}
              <div className={styles.content}>
                <div className={styles.memberDetails}>
                  {this.renderName()}
                  {showTitle && this.renderTitle()}
                  {isSocial &&
                    showFollowers &&
                    inCommunity &&
                    this.renderFollowers()}
                  {isSocial && showAsBlocked && this.renderBlocked()}
                  {badgesSettings.badges.length > 0 && this.renderBadges()}
                </div>
                {this.renderButtons()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  private readonly getWidgetSizeClass = (dimensions?: {
    width: number;
    height: number;
  }) => {
    if (!dimensions?.width) {
      return styles.fullWidth;
    }

    if (dimensions.width >= WIDE_WIDGET_BREAKPOINT) {
      return styles.large;
    }

    return '';
  };

  private readonly handleCoverPositionChange = (event: Event) => {
    const { host, handlers } = this.props;
    const { changeCoverPosition } = handlers;
    const coverWidth = host?.dimensions?.width ?? 0;

    forwardCoverRepositionAttributes(changeCoverPosition, coverWidth)(event);
  };

  private readonly startRepositioningCover = () => {
    const { profilePage } = this.props;
    const { isCoverRepositionMode } = profilePage;

    if (isCoverRepositionMode && this.coverRef.current) {
      this.coverRef.current.addEventListener(
        'mousemove',
        this.handleCoverPositionChange,
      );
      this.setState({ isMovingCover: true });
    }
  };

  private readonly stopRepositioningCover = () => {
    const { profilePage } = this.props;
    const { isCoverRepositionMode } = profilePage;

    if (isCoverRepositionMode && this.coverRef.current) {
      this.coverRef.current.removeEventListener(
        'mousemove',
        this.handleCoverPositionChange,
      );
      this.setState({ isMovingCover: false });
    }
  };

  private readonly renderLoader = () => (
    <LoaderOverlay dataHook={DataHook.Loader} className={styles.loader} />
  );

  private readonly renderCover = () => {
    const { profilePage, t } = this.props;
    const { isMovingCover } = this.state;
    const { isCoverLoading, isCoverRepositionMode } = profilePage;

    return (
      <div data-hook={DataHook.CoverWrapper} className={styles.coverWrapper}>
        <div
          ref={this.coverRef}
          className={styles.coverBorderMargin}
          onMouseDown={this.startRepositioningCover}
          onMouseUp={this.stopRepositioningCover}
          onMouseLeave={this.stopRepositioningCover}
        >
          {this.renderCoverImage()}
          {isCoverLoading && this.renderLoader()}
          {isCoverRepositionMode && !isCoverLoading && (
            <RepositionButton
              dataHook={DataHook.DragToChangeCoverPositionCTA}
              className={classNames(
                styles.repositionCoverButton,
                isMovingCover && styles.isMoving,
              )}
              text={t('profile-widget.drag-to-reposition')}
            />
          )}
        </div>
        {this.renderCoverButtons()}
      </div>
    );
  };

  private readonly renderPhoto = () => {
    const { compId, member, profilePage, computed, handlers, t } = this.props;
    const { isProfilePreview, isEditing, editPicture } = profilePage;
    const { pictureStyle, canEdit } = computed;
    const photoInputId = getElementId(compId, ElementId.ProfilePhotoInput);

    const { setMemberPicture, setEditPicture } = handlers;
    const handlePhotoChange = forwardResizedImage(setMemberPicture);
    const handlePhotoUpload = forwardResizedImage(setEditPicture);

    return (
      <Avatar
        dataHook={DataHook.ProfilePhoto}
        className={styles.profilePhoto}
        name={member.name}
        layoutType={pictureStyle}
        url={(editPicture && editPicture.file) || member.picture}
      >
        {!isProfilePreview && canEdit && <div className={styles.overlay} />}
        {!isProfilePreview && canEdit && (
          <div className={styles.profilePhotoIco}>
            <input
              id={photoInputId}
              className={styles.uploadInput}
              type="file"
              accept="image/*"
              aria-label={t('profile-widget.change-profile-photo')}
              onChange={isEditing ? handlePhotoUpload : handlePhotoChange}
            />
            <label htmlFor={photoInputId} className={styles.uploadLabel} />
          </div>
        )}
      </Avatar>
    );
  };

  private readonly renderName = () => {
    const { member, site, profilePage, rolesMap, handlers, t } = this.props;
    const { isSocial } = site;
    const { isEditing, editName } = profilePage;
    const name = editName ?? member.name;

    const handleInputChange = forwardTargetValue(handlers.setEditName);

    return (
      <div data-hook={DataHook.MemberName} className={styles.nameBox}>
        <div className={styles.nameWrapper}>
          {isEditing && (
            <div className={styles.nameBoxText}>
              <input
                data-hook={DataHook.MemberNameInput}
                ref={this.memberNameInputRef}
                className={styles.nameBoxInput}
                maxLength={50}
                size={50}
                defaultValue={name}
                onChange={handleInputChange}
              />
            </div>
          )}
          {!isEditing && (
            <MemberName
              className={styles.nameBoxText}
              tooltipClass={styles.tooltipWrapper}
              name={name}
            />
          )}
          <div className={styles.inputBorder} />
        </div>
        {isSocial && (
          <RoleIcons
            containerClassName={styles.roleIcons}
            iconClassName={styles.roleIcon}
            roles={member.roles}
            rolesMap={rolesMap}
            withNames={true}
            t={t}
          />
        )}
      </div>
    );
  };

  private readonly renderTitle = () => {
    const { member, profilePage, handlers, t } = this.props;
    const { isEditing, editTitle } = profilePage;
    const title = editTitle ?? member.title;
    const handleTitleChange = forwardTargetValue(handlers.setEditTitle);

    return (
      <Fragment>
        {isEditing && (
          <div className={styles.titleWrapper}>
            <input
              className={classNames(styles.titleBoxInput, 'has-custom-focus')}
              size={titleMaxLength}
              maxLength={titleMaxLength}
              placeholder={t('profile-widget.title-placeholder')}
              defaultValue={title ?? ''}
              onChange={handleTitleChange}
            />
            <LimitCounter
              className={styles.limitCounter}
              length={title?.length ?? 0}
              limit={titleMaxLength}
            />
            <div className={styles.inputBorder} />
          </div>
        )}
        {!isEditing && title && (
          <div className={styles.titleWrapper}>
            <p className={classNames(styles.title)}>{title}</p>
          </div>
        )}
      </Fragment>
    );
  };

  private readonly renderFollowers = () => {
    const { member, handlers, computed, t } = this.props;
    const { followersInstalled } = computed;

    const handleFollowersBoxClick = absorbArguments(() => {
      handlers.navigateToViewedMemberFFPage(true);
    });

    const handleFollowingBoxClick = absorbArguments(() => {
      handlers.navigateToViewedMemberFFPage(false);
    });

    return (
      <div
        data-hook={DataHook.FollowersFollowing}
        className={classNames(styles.numbersBox)}
      >
        <div
          data-hook={DataHook.Followers}
          className={styles.followersBoxContainer}
        >
          <Button
            className={classNames(styles.numbersBoxFollowers, {
              [styles.disabled]: !followersInstalled,
            })}
            onClick={handleFollowersBoxClick}
            isDisabled={!followersInstalled}
          >
            <span>{member.followerCount}</span>
            <div className={styles.numbersBoxText}>
              {t('profile-widget.followers')}
            </div>
          </Button>
        </div>
        <div className={styles.divider} />
        <div data-hook={DataHook.Following}>
          <Button
            className={classNames(styles.numbersBoxFollowing, {
              [styles.disabled]: !followersInstalled,
            })}
            onClick={handleFollowingBoxClick}
            isDisabled={!followersInstalled}
          >
            <span>{member.followingCount}</span>
            <div className={styles.numbersBoxText}>
              {t('profile-widget.following')}
            </div>
          </Button>
        </div>
      </div>
    );
  };

  private readonly renderBlocked = () => {
    const { t } = this.props;
    return <Blocked className={styles.blocked} t={t} />;
  };

  private readonly renderBadges = () => {
    const { site, computed, handlers } = this.props;
    const { navigateToViewedMember } = handlers;
    const { badgesSettings } = computed;
    const { isSocial } = site;

    return (
      <div className={styles.badgesContainer}>
        <BadgeList
          {...badgesSettings}
          align={'left'}
          maxRows={1}
          onClickMore={isSocial ? navigateToViewedMember : undefined}
          onBadgeClick={isSocial ? navigateToViewedMember : undefined}
        />
      </div>
    );
  };

  private readonly renderButtons = () => {
    const { site, profilePage, computed, handlers, t } = this.props;
    const {
      inCommunity,
      showAsBlocked,
      canEdit,
      isDesignPreview,
      isInProfilePage,
    } = computed;
    const { isEditing, isProfilePreview } = profilePage;
    const { isSocial } = site;

    const {
      saveProfile,
      stopEditingProfile,
      toggleIsEditingProfile,
      togglePublicProfilePreview,
    } = handlers;

    if (isEditing || isDesignPreview) {
      const handleCancelButtonClick = absorbArguments(stopEditingProfile);
      const handleSaveButtonClick = absorbArguments(saveProfile);

      return (
        <div className={styles.buttonsBox}>
          {this.renderButton({
            className: styles.truncateText,
            title: t('profile-widget.cancel'),
            isSecondary: true,
            onClick: handleCancelButtonClick,
          })}

          {this.renderButton({
            dataHook: DataHook.SaveProfileCTA,
            className: styles.truncateText,
            title: t('profile-widget.save'),
            onClick: handleSaveButtonClick,
          })}
          {this.renderActions()}
        </div>
      );
    }

    if (canEdit && !isProfilePreview) {
      const { showViewPublicProfileCTA, showEditProfileCTA } = computed;
      const isInSocialCommunity = inCommunity && isSocial;
      const handleEditButtonClick = absorbArguments(toggleIsEditingProfile);
      const handleViewPublicProfileButtonClick = () => {
        togglePublicProfilePreview();
      };
      const handleCommunityButtonClick = () => {
        handlers.executeRoleAction(RoleId.JOIN_COMMUNITY);
      };

      return (
        <div className={styles.buttonsBox}>
          {isSocial && !inCommunity && !showAsBlocked && isInProfilePage && (
            <div
              data-hook={DataHook.JoinCommunityCTA}
              className={styles.mainButton}
            >
              {this.renderButton({
                className: styles.truncateText,
                title: t('MemberRoles.action_set.community'),
                isSecondary: true,
                onClick: handleCommunityButtonClick,
              })}
            </div>
          )}
          {isInSocialCommunity && showEditProfileCTA && (
            <div className={styles.mainButton}>
              {this.renderButton({
                dataHook: DataHook.EditProfileCTA,
                className: classNames(
                  styles.truncateText,
                  styles.editProfileButton,
                ),
                title: t('profile-widget.my-profile'),
                isSecondary: true,
                onClick: handleEditButtonClick,
              })}
            </div>
          )}
          {isInSocialCommunity && showViewPublicProfileCTA && (
            <div className={styles.mainButton}>
              {this.renderButton({
                dataHook: DataHook.ViewPubLicProfileCTA,
                className: classNames(
                  styles.truncateText,
                  styles.editProfileButton,
                ),
                title: t('profile-widget.view-public-profile'),
                isSecondary: true,
                onClick: handleViewPublicProfileButtonClick,
              })}
            </div>
          )}
          {this.renderActions()}
        </div>
      );
    }

    return (
      <div className={styles.buttonsBox}>
        {!showAsBlocked && (
          <Fragment>
            <div className={classNames(styles.mainButton, styles.chatButton)}>
              {computed.allowChat && this.renderChatButton()}
            </div>
            <div className={classNames(styles.mainButton, styles.followButton)}>
              {this.renderFollowUnfollowButton()}
            </div>
          </Fragment>
        )}
        {this.renderActions()}
      </div>
    );
  };

  private readonly renderCoverImage = () => {
    const { isMovingCover } = this.state;
    const { member, profilePage, globalSettings, isMobile } = this.props;
    const { defaultProfileCoverUrl } = globalSettings;
    const { editCover } = profilePage;

    const coverUrl = getCoverUrl({
      member,
      editCover,
      isMobile,
      defaultCoverUrl: defaultProfileCoverUrl,
      profileLayout: ProfileLayout.FullWidth,
      isFullWidth: this.props.computed.isResponsiveEditor,
    });

    const coverStyle = getCoverImageStyle({
      coverUrl,
      member,
      editCover,
    });

    return (
      <div
        data-hook={DataHook.HighQualityCover}
        className={classNames(styles.cover, !coverStyle && styles.coverColor)}
        style={coverStyle || {}}
      >
        {!isMovingCover && (
          <div
            className={classNames(
              styles.coverOverlay,
              !coverStyle && styles.withoutPhoto,
            )}
          />
        )}
      </div>
    );
  };

  private readonly renderCoverButtons = () => {
    const { isMovingCover } = this.state;
    const { member, profilePage, computed, handlers, t } = this.props;
    const { isCoverRepositionMode, isProfilePreview } = profilePage;
    const { canEdit } = computed;
    const coverPhotoInputId = getElementId(
      this.props.compId,
      ElementId.CoverPhotoInput,
    );

    const {
      setEditCover,
      enterCoverRepositionMode,
      leaveCoverRepositionMode,
      saveCoverReposition,
    } = handlers;

    const handleCoverUpload: ChangeEventHandler<HTMLInputElement> = (event) => {
      enterCoverRepositionMode();
      forwardResizedImage(setEditCover)(event);
    };

    const handleRepositionButtonClick = () => {
      enterCoverRepositionMode();
    };

    const handleCoverRepositionCancel = absorbArguments(
      leaveCoverRepositionMode,
    );

    const handleCoverRepositionSave = absorbArguments(saveCoverReposition);

    return (
      <Fragment>
        {canEdit && !isCoverRepositionMode && !isProfilePreview && (
          <div
            className={classNames(styles.coverPhotoContainer, styles.canEdit)}
          >
            <input
              id={coverPhotoInputId}
              className={styles.uploadInput}
              type="file"
              accept="image/*"
              aria-label={t('profile-widget.change-aria-label-photo')}
              onChange={handleCoverUpload}
            />
            <div
              data-hook={DataHook.ChangeCoverCTA}
              className={styles.coverPhotoChange}
            >
              <p className={styles.uploadText}>
                {t('profile-widget.change-photo')}
              </p>
              <div className={styles.coverPhotoIco} />
              <label
                htmlFor={coverPhotoInputId}
                className={styles.uploadLabel}
              />
            </div>
            {!!member.cover && (
              <RepositionButton
                dataHook={DataHook.ChangeCoverPositionCTA}
                className={styles.enterCoverRepositionButton}
                text={t('profile-widget.reposition')}
                onClick={handleRepositionButtonClick}
              />
            )}
          </div>
        )}

        {isCoverRepositionMode && !isMovingCover && (
          <div className={styles.repositionModeButtons}>
            <div className={styles.buttonsBox}>
              {this.renderButton({
                className: styles.truncateText,
                title: t('profile-widget.cancel'),
                isSecondary: true,
                onClick: handleCoverRepositionCancel,
              })}
              {this.renderButton({
                className: styles.truncateText,
                title: t('profile-widget.done'),
                onClick: handleCoverRepositionSave,
              })}
            </div>
          </div>
        )}
      </Fragment>
    );
  };

  private readonly renderChatButton = () => {
    const { profilePage, handlers, t } = this.props;
    const handleChatButtonClick = absorbArguments(handlers.openChat);

    return (
      <Button
        className={styles.buttonSecondary}
        isDisabled={profilePage.isProfilePreview}
        onClick={handleChatButtonClick}
      >
        {t('profile-widget.message')}
      </Button>
    );
  };

  private readonly renderFollowUnfollowButton = () => {
    const { member, profilePage, handlers, t } = this.props;

    const { followOrUnfollow } = handlers;
    const handleFollowButtonClick = absorbArguments(followOrUnfollow);

    return (
      <FollowButton
        dataHook={DataHook.FollowCTA}
        className={styles.buttonSecondary}
        followText={t('profile-widget.follow')}
        unfollowText={t('profile-widget.unfollow')}
        type="text"
        isFollowing={member.isSubscribed}
        isDisabled={profilePage.isProfilePreview}
        onClick={handleFollowButtonClick}
      />
    );
  };

  private readonly renderActions = () => {
    const { member, isMobile, isRTL, computed, handlers, t } = this.props;
    const { additionalActions, rolesActions, isResponsiveEditor } = computed;
    const { isProfilePreview } = this.props.profilePage;
    const { uid } = member;

    if (rolesActions.length === 0 && additionalActions.length === 0) {
      return;
    }

    const handleRoleAction = (roleId: RoleId) => {
      if (roleId === RoleId.CONTACT_PAGE) {
        const { metaSiteId } = this.props;
        return maybeOpenContactsWindow(metaSiteId, uid, isResponsiveEditor);
      }

      handlers.executeRoleAction(roleId);
    };

    return (
      <MemberMoreButton
        containerClass={styles.moreActions}
        additionalActions={additionalActions}
        rolesActions={rolesActions}
        position={Position.Bottom}
        alignment={Position.Right}
        isMobile={isMobile}
        isRtlLocale={isRTL}
        isDisabled={isProfilePreview}
        dynamicPositioning={false}
        t={t}
        onAdditionalAction={handlers.executeAdditionalAction}
        onRoleAction={handleRoleAction}
      />
    );
  };

  private readonly renderButton = (props: RenderButtonProps) => {
    const className = props.className ?? defaultRenderButtonProps.className;
    const baseClassName = classNames(styles.mainButtonElement, className);
    const buttonClassName = props.isSecondary
      ? classNames(baseClassName, styles.buttonSecondary)
      : classNames(baseClassName, styles.buttonPrimary);

    const handleButtonClick: SubmitButtonClickHandler = (event) => {
      event.preventDefault();
      props.onClick(event);
    };

    return (
      <Button
        dataHook={props.dataHook}
        className={buttonClassName}
        isDisabled={props.isDisabled}
        onClick={handleButtonClick}
      >
        {props.title}
      </Button>
    );
  };
}

export default DesktopResponsiveWidget;
